import { IScheduledJob } from "@kuika/kuika-cl-model";
import cronstrue from "cronstrue";
import { KMainFunctions } from "../../../shared/utility/main-functions";

/**
 * Ready-made schedules for the Scheduled Jobs landing page.
 *
 * The catalog is a module rather than backend content because an entry is four literal
 * fields — a name, a cron expression, a timezone and a sentence — with nothing a project,
 * an environment or a tenant could vary. That is the same call the Email Builder's catalog
 * makes; the process-template catalog lives in `Metadatas/` instead because its entries
 * carry whole workflow json/xml payloads that are edited as content.
 *
 * What a template deliberately does NOT carry is `actions`. A job's actions are
 * `actionDefinitionId` references into the project's own action set, so a static catalog
 * cannot name one: a template supplies the SCHEDULE, and the editor is where the work that
 * runs on it gets attached.
 */

export interface IScheduledJobTemplate {
  id: string;
  /** Card title. */
  name: string;
  /** Seed for the created job's name; must satisfy the editor's name rule. */
  defaultJobName: string;
  description: string;
  /** Browsing category, shown as a filter chip. */
  tag: string;
  /** Quartz expression, in the 7-field form the cron builder itself emits. */
  cronValue: string;
  /** IANA id, as `gmtZones.json` stores them. */
  timeZone: string;
  /** Glyph for the card's cover band; a name under `public/images/common`. */
  iconName: string;
}

/**
 * Twelve starting points, ordered by CADENCE — every fifteen minutes at the top, quarterly
 * at the bottom. That is the order because it is the question people actually arrive with:
 * not "what category is this" but "how often should it run". The tag chips are there for
 * the other way in.
 *
 * The crons are written the way `@sbzen/re-cron` emits them — day-of-month `?` with
 * day-of-week `*` — except the entries that pin a weekday or a date, which have to invert
 * that pair, since Quartz requires exactly one of the two to be `?`.
 */
export const SCHEDULED_JOB_TEMPLATE_CATALOG: IScheduledJobTemplate[] = [
  {
    id: "health-check",
    name: "Health Check",
    defaultJobName: "Health Check",
    description: "Ping a service every quarter of an hour and raise the alarm while it is still cheap to fix.",
    tag: "Monitoring",
    cronValue: "0 0/15 * ? * * *",
    timeZone: "Europe/London",
    iconName: "shield-check"
  },
  {
    id: "session-cleanup",
    name: "Session Cleanup",
    defaultJobName: "Session Cleanup",
    description: "Drop expired sessions and stale tokens four times a day, so nothing lingers past its welcome.",
    tag: "Maintenance",
    cronValue: "0 0 0/6 ? * * *",
    timeZone: "Europe/London",
    iconName: "key-off"
  },
  {
    id: "hourly-sync",
    name: "Hourly Sync",
    defaultJobName: "Hourly Sync",
    description: "Pull changes from an external system every hour so your data never drifts far.",
    tag: "Integration",
    cronValue: "0 0 * ? * * *",
    timeZone: "Europe/London",
    iconName: "refresh"
  },
  {
    id: "nightly-backup",
    name: "Nightly Backup",
    defaultJobName: "Nightly Backup",
    description: "Take the nightly copy at half past two, before the cleanup jobs start moving things.",
    tag: "Maintenance",
    cronValue: "0 30 2 ? * * *",
    timeZone: "Europe/London",
    iconName: "archive"
  },
  {
    id: "daily-cleanup",
    name: "Daily Cleanup",
    defaultJobName: "Daily Cleanup",
    description: "Clear out expired records, temporary files and stale logs while nobody is working.",
    tag: "Maintenance",
    cronValue: "0 0 3 ? * * *",
    timeZone: "Europe/London",
    iconName: "trash"
  },
  {
    id: "daily-digest",
    name: "Daily Digest",
    defaultJobName: "Daily Digest",
    description: "Mail yesterday's numbers out at half past seven, in time to be read over coffee.",
    tag: "Notifications",
    cronValue: "0 30 7 ? * * *",
    timeZone: "Europe/London",
    iconName: "mail"
  },
  {
    id: "weekday-reminder",
    name: "Weekday Reminder",
    defaultJobName: "Weekday Reminder",
    description: "Nudge people at nine on working days only — no weekend notifications.",
    tag: "Notifications",
    cronValue: "0 0 9 ? * MON-FRI *",
    timeZone: "Europe/London",
    iconName: "bell"
  },
  {
    id: "end-of-day-summary",
    name: "End Of Day Summary",
    defaultJobName: "End Of Day Summary",
    description: "Close the books on the day at 23:55, while it is still the same date everywhere it matters.",
    tag: "Reporting",
    cronValue: "0 55 23 ? * * *",
    timeZone: "Europe/London",
    iconName: "file-time"
  },
  {
    id: "weekly-report",
    name: "Weekly Report",
    defaultJobName: "Weekly Report",
    description: "Send the week's summary on Monday morning, before the first meeting.",
    tag: "Reporting",
    cronValue: "0 0 8 ? * MON *",
    timeZone: "Europe/London",
    iconName: "file-description"
  },
  {
    id: "invoice-run",
    name: "Invoice Run",
    defaultJobName: "Invoice Run",
    description: "Raise the month's invoices on the 25th, leaving room to fix them before month end.",
    tag: "Finance",
    cronValue: "0 0 9 25 * ? *",
    timeZone: "Europe/London",
    iconName: "report-money"
  },
  {
    id: "monthly-close",
    name: "Monthly Close",
    defaultJobName: "Monthly Close",
    description: "Run the month-end job — archiving, closing balances, carrying figures forward — on the first.",
    tag: "Finance",
    cronValue: "0 0 1 1 * ? *",
    timeZone: "Europe/London",
    iconName: "calendar-dollar"
  },
  {
    id: "quarterly-report",
    name: "Quarterly Report",
    defaultJobName: "Quarterly Report",
    description: "Produce the quarter's figures on the first of January, April, July and October.",
    tag: "Reporting",
    cronValue: "0 0 6 1 1,4,7,10 ? *",
    timeZone: "Europe/London",
    iconName: "calendar-event"
  }
];

/**
 * The colour ramp a tag's cards are tinted with.
 *
 * Per TAG rather than per entry, so the grid gains rhythm AND the tint means something —
 * two cards the same colour are two cards in the same category. Red is deliberately unused:
 * it reads as danger everywhere else in the designer.
 *
 * Unknown tags fall back to the card's own neutral band, which is the honest answer for a
 * category nobody has assigned a colour to.
 */
const TAG_RAMPS: Record<string, string> = {
  Monitoring: "green",
  Maintenance: "blue",
  Integration: "purple",
  Notifications: "orange",
  Reporting: "pink",
  Finance: "yellow"
};

export const scheduledJobTagRamp = (tag: string): string | undefined => TAG_RAMPS[tag];

/**
 * A cron expression as a sentence — "At 03:00, only on Monday".
 *
 * This is the whole reason the expression is not the only thing on screen: every
 * scheduler worth copying shows the expression and its plain-English reading together,
 * because that pairing is what teaches the syntax while you build it.
 *
 * Returns undefined rather than throwing for anything unreadable. The builder emits
 * intermediate states while the user is still choosing, and a half-built expression must
 * degrade to "no sentence yet", never to an error boundary.
 */
export const describeCronValue = (cronValue?: string): string | undefined => {
  if (!cronValue || cronValue.trim().length === 0) return undefined;
  try {
    return cronstrue.toString(cronValue, { use24HourTimeFormat: true, throwExceptionOnParseError: true });
  } catch {
    return undefined;
  }
};

/** The tags present in a set of templates, in catalog order, each with its count. */
export interface IScheduledJobTemplateTag {
  value: string;
  count: number;
}

export const scheduledJobTemplateTags = (templates: IScheduledJobTemplate[]): IScheduledJobTemplateTag[] => {
  const counts: Map<string, number> = new Map<string, number>();
  templates.forEach((template: IScheduledJobTemplate): void => {
    counts.set(template.tag, (counts.get(template.tag) ?? 0) + 1);
  });
  return Array.from(counts.entries()).map(([value, count]: [string, number]): IScheduledJobTemplateTag => ({
    value,
    count
  }));
};

/**
 * Search across everything a card shows — including the schedule sentence, so "monday"
 * finds the weekly template even though neither its name nor its description says it.
 */
export const filterScheduledJobTemplates = (
  templates: IScheduledJobTemplate[],
  searchText: string
): IScheduledJobTemplate[] => {
  const needle: string = KMainFunctions.turkishCharsToEnglish(searchText).trim().toLowerCase();
  if (needle.length === 0) return templates;
  return templates.filter((template: IScheduledJobTemplate): boolean => {
    const haystack: string = [
      template.name,
      template.description,
      template.tag,
      describeCronValue(template.cronValue) ?? ""
    ]
      .join(" ")
      .toLowerCase();
    return haystack.indexOf(needle) !== -1;
  });
};

/**
 * Alphabetical, and the one comparator the landing table and the editor's job switcher
 * share, so the list you pick from is ordered the same way in both places.
 */
export const sortScheduledJobs = (jobs: IScheduledJob[]): IScheduledJob[] =>
  [...jobs].sort((a: IScheduledJob, b: IScheduledJob): number => a.name.localeCompare(b.name, "en"));
